import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { ComponentBatch, ComponentBatchesState } from '../../types/componentBatch';
import { componentBatchApiService } from '../../services/componentBatchApiService';
import { componentBatchIngredientApiService } from '../../services/componentBatchIngredientApiService';
import { ComponentIngredient } from '../../types/componentRecord';
import { RootState } from '../store';

// Helper function to create a blank component batch
export const createBlankComponentBatch = (): ComponentBatch => {
  const today = new Date().toISOString().split('T')[0];
  return {
    id: `temp-${uuidv4()}`,  // Use temp- prefix so isTempRecord check works
    productName: '',
    productId: '',
    dateOfProduction: today,
    batchLotNumber: '',
    preparedBy: '',
    verifiedBy: '',
    timesProduced: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
};

// Async thunks

/**
 * Fetch all component batches from the API
 */
export const fetchComponentBatches = createAsyncThunk(
  'componentBatches/fetchComponentBatches',
  async () => {
    return await componentBatchApiService.fetchComponentBatches();
  }
);

interface CreateComponentBatchPayload {
  batch: ComponentBatch;
  ingredients: ComponentIngredient[];
}

/**
 * Create a new component batch with its ingredients
 */
export const createComponentBatchAsync = createAsyncThunk(
  'componentBatches/createComponentBatch',
  async (payload: CreateComponentBatchPayload, { getState }) => {
    const { batch, ingredients } = payload;

    console.log('[createComponentBatchAsync] Starting batch creation');
    console.log('[createComponentBatchAsync] Original batch ID (temp):', batch.id);
    console.log('[createComponentBatchAsync] Ingredients to save:', ingredients.length);

    // First, create the component batch to get the real ID
    const createdBatch = await componentBatchApiService.createComponentBatch(batch);
    const realBatchId = createdBatch.id;

    console.log('[createComponentBatchAsync] Batch created with real ID:', realBatchId);

    // Get raw ingredients from state for lookup
    const state = getState() as RootState;
    const allRawIngredients = state.rawIngredients.ingredients;

    // Save all ingredients with the real batch ID
    if (ingredients.length > 0) {
      console.log('[createComponentBatchAsync] Saving ingredients with componentBatchId:', realBatchId);
      await Promise.all(
        ingredients.map(async (ing) => {
          // Find the rawIngredientId by ingredient name
          const rawIngredient = allRawIngredients.find(ri => ri.name === ing.ingredientName);
          if (!rawIngredient) {
            console.error(`Raw ingredient not found for name: ${ing.ingredientName}`);
            return;
          }

          console.log('[createComponentBatchAsync] Creating ingredient:', ing.ingredientName, 'with componentBatchId:', realBatchId);
          return componentBatchIngredientApiService.createComponentBatchIngredient({
            componentBatchId: realBatchId,
            rawIngredientId: rawIngredient.id,
            lotNumbers: Array.isArray(ing.lotNumbers) ? ing.lotNumbers : [ing.lotNumbers],
            quantity: ing.quantity ?? null,
            unit: ing.unit ?? null
          });
        })
      );
      console.log('[createComponentBatchAsync] All ingredients saved successfully');
    }

    console.log('[createComponentBatchAsync] Batch creation complete, returning batch with ID:', createdBatch.id);
    return createdBatch;
  }
);

/**
 * Update an existing component batch
 */
export const updateComponentBatchAsync = createAsyncThunk(
  'componentBatches/updateComponentBatch',
  async (componentBatch: ComponentBatch) => {
    return await componentBatchApiService.updateComponentBatch(componentBatch);
  }
);

/**
 * Delete a component batch
 */
export const deleteComponentBatchAsync = createAsyncThunk(
  'componentBatches/deleteComponentBatch',
  async (id: string, { rejectWithValue }) => {
    try {
      await componentBatchApiService.deleteComponentBatch(id);
      return id;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete component batch');
    }
  }
);

/**
 * Fetch a single component batch by ID
 */
export const fetchComponentBatchById = createAsyncThunk(
  'componentBatches/fetchComponentBatchById',
  async (id: string) => {
    return await componentBatchApiService.fetchComponentBatchById(id);
  }
);

// Initial state
const initialState: ComponentBatchesState = {
  items: [],
  loading: false,
  error: null,
};

const componentBatchSlice = createSlice({
  name: 'componentBatches',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Fetch component batches
    builder
      .addCase(fetchComponentBatches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchComponentBatches.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchComponentBatches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch component batches';
      });

    // Create component batch
    builder
      .addCase(createComponentBatchAsync.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(createComponentBatchAsync.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to create component batch';
      });

    // Update component batch
    builder
      .addCase(updateComponentBatchAsync.fulfilled, (state, action) => {
        const index = state.items.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateComponentBatchAsync.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update component batch';
      });

    // Delete component batch
    builder
      .addCase(deleteComponentBatchAsync.fulfilled, (state, action) => {
        state.items = state.items.filter(b => b.id !== action.payload);
      })
      .addCase(deleteComponentBatchAsync.rejected, (state, action) => {
        state.error = (action.payload as string) || action.error.message || 'Failed to delete component batch';
      });

    // Fetch component batch by ID
    builder
      .addCase(fetchComponentBatchById.fulfilled, (state, action) => {
        // Check if batch already exists to prevent duplicates
        const existingIndex = state.items.findIndex(b => b.id === action.payload.id);
        if (existingIndex === -1) {
          state.items.push(action.payload);
        } else {
          // Update existing batch if found
          state.items[existingIndex] = action.payload;
        }
      })
      .addCase(fetchComponentBatchById.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch component batch by ID';
      });
  }
});

export default componentBatchSlice.reducer;
